import ErrorBoundary from '@/components/ErrorBoundary'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useTourHandler } from '@/contexts/tour-handler'
import ProductTemplate from '@/modules/Contract/components/New/ProductTemplate'
import {
    buildServiceConfigoptions,
    buildServiceTimes,
} from '@/modules/Contract/components/New/reformat'
import { initServiceTimes } from '@/modules/Contract/constants/function'
import { Contract } from '@/modules/Contract/types'
import { createServiceAddons, getService } from '@/modules/Service/services'
import { generatePathDatacentral, paths } from '@/utils/path'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Alert, AlertError, Chip, GridHookForm, Typogaphy } from 'vnetwork-ui'
import { SERVICE_STATUS } from '../../constants'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import toast from 'react-hot-toast'
import createAddonSchema from './schema'
import useCreateAddonsTour from './use-create-addons-tour'

const CreateAddons = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [search] = useSearchParams()
    const service_id = Number(search.get('service_id') || 0)
    const { registerHandler, unregisterHandler } = useTourHandler()
    const { startTour } = useCreateAddonsTour()

    // Register tour handler
    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    const { data: service, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICE, service_id],
        queryFn: () => getService(service_id),
        enabled: !!service_id,
    })

    const today = dayjs()
    // Default addon end = parent's CURRENT period end (firmly committed period),
    // not the next renewal end. Prevents addon from outlasting the parent's commitment.
    const current_enddate = service?.current_enddate
        ? dayjs(service.current_enddate)
        : today

    const formControl = useForm<Contract>({
        defaultValues: {
            customer_id: service?.customer_id || 0,
            contract_effective_date: today,
            contract_expiry_date: current_enddate,
            contract_value: 0,
            contract_note: '',
            customer_contact_id: service?.contact_id || 0,
            customer_journey_id: 0,
            employee_sale_tracking_ids:
                service?.employee_sale_tracking_ids || [],
            order: {
                order_source: 'dc',
                order_amount: 0,
                order_note: '',
                order_source_name: 'customer',
                products: [
                    {
                        product_id: 0,
                        product_group_name: service?.product_group_name || '',
                        product_form: 'commit',
                        service_times: initServiceTimes(today, current_enddate),
                        service_price_register: 0,
                        editable: true,
                        prepaid: true,
                        service_price_model: 'package',
                    },
                ],
            },
        },
        // @ts-ignore
        resolver: yupResolver(createAddonSchema),
    })

    const { fields, remove } = useFieldArray({
        name: 'order.products',
        control: formControl.control,
    })

    const products = useWatch({
        control: formControl.control,
        name: 'order.products',
    })

    // Warn (but don't block) when the addon's end date exceeds parent's
    // current period end — addon would outlast the parent's committed term.
    const addonProduct = products?.[0]
    const addonEndRaw = addonProduct?.service_times?.first_enddate
    const addonStartRaw = addonProduct?.service_times?.first_startdate
    const parentCurrentEnd = service?.current_enddate
        ? dayjs(service.current_enddate)
        : null
    const addonEnd = addonEndRaw ? dayjs(addonEndRaw) : null
    const addonStart = addonStartRaw ? dayjs(addonStartRaw) : null
    const exceedsParent = !!(
        parentCurrentEnd &&
        addonEnd &&
        addonEnd.isAfter(parentCurrentEnd, 'day')
    )

    // Warn when any configoption's commitment_time doesn't match the addon
    // period length — register invoice would over/under-bill (formula:
    // amount = commitment_value / commitment_time * period_months * ppu).
    const periodMonths =
        addonStart && addonEnd ? addonEnd.diff(addonStart, 'month') : 0
    const configoptions = addonProduct?.service_configoptions ?? []
    const mismatchedConfig = configoptions.find(
        (c) =>
            c?.commitment_time != null &&
            Number(c.commitment_time) !== periodMonths
    )
    const commitmentMismatch = !!mismatchedConfig && periodMonths > 0

    const { mutateAsync, isPending: isCreating } = useMutationWithHandler(
        { mutationFn: createServiceAddons },
        {
            successMessage: t('create_service_addons_success'),
            reactQueryKeys: QUERY_KEYS.SERVICES,
            onSuccess: () => {
                navigate(
                    generatePathDatacentral(paths.serviceDetail, {
                        id: service_id,
                    })
                )
            },
        }
    )

    const handleSubmit = (values: Contract) => {
        const addonProduct = values.order.products[0]
        mutateAsync({
            addons: {
                ...addonProduct,
                service_parent_id: service_id,
                service_name: addonProduct?.product_name,
                service_times: buildServiceTimes(addonProduct?.service_times),
                service_configoptions: buildServiceConfigoptions(
                    addonProduct?.service_configoptions
                ),
                employee_sale_tracking_ids:
                    values?.employee_sale_tracking_ids || [],
            },
        })
    }

    const handleCancel = () => {
        navigate(-1)
    }

    // Surface lỗi validate khi submit thất bại. GridHookForm gọi
    // handleSubmit(onSubmit) không kèm onInvalid, nên nếu không có đoạn này thì
    // mọi lần fail validate đều im lặng (đúng triệu chứng bug gốc). Chỉ phụ thuộc
    // submitCount để không spam toast mỗi lần gõ (reValidateMode mặc định onChange
    // sau lần submit đầu tiên).
    const { submitCount, errors } = formControl.formState
    useEffect(() => {
        if (submitCount > 0 && Object.keys(errors).length > 0) {
            toast.error(t('validate-warning'))
        }
    }, [submitCount])

    // Update form when service data loads
    useEffect(() => {
        if (service) {
            const enddate = service?.current_enddate
                ? dayjs(service.current_enddate)
                : today

            formControl.reset({
                customer_id: service.customer_id,
                contract_effective_date: today,
                contract_expiry_date: enddate,
                contract_value: 0,
                contract_note: '',
                customer_contact_id: service.contact_id || 0,
                customer_journey_id: 0,
                // Prefill người hưởng hoa hồng từ service cha. Trước đây reset bỏ
                // sót field top-level này (chỉ set trong products) nên sau khi
                // service load field bị xoá trắng → payload gửi [] → BE 400.
                employee_sale_tracking_ids:
                    service.employee_sale_tracking_ids || [],
                order: {
                    order_source: 'dc',
                    order_amount: 0,
                    order_note: '',
                    order_source_name: 'customer',
                    products: [
                        {
                            product_id: 0,
                            product_group_name: service.product_group_name,
                            product_form: 'commit',
                            service_times: initServiceTimes(today, enddate),
                            service_price_register: 0,
                            editable: true,
                            prepaid: true,
                            service_price_model: 'package',
                            employee_sale_tracking_ids:
                                service.employee_sale_tracking_ids || [],
                        },
                    ],
                },
            })
        }
    }, [service])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    if (!service_id) {
        return (
            <div className="p-4">
                <AlertError
                    error={new Error(t('missing-required-parameters'))}
                />
            </div>
        )
    }

    if (!service?.product_group_name) {
        return (
            <div className="p-4">
                <AlertError error={new Error(t('service-not-found'))} />
            </div>
        )
    }

    return (
        <ErrorBoundary>
            <FormProvider {...formControl}>
                <SubHeaders
                    actions={[]}
                    breadcrumbs={[
                        {
                            path: generatePathDatacentral(paths.services, {}),
                            title: t('service-list'),
                        },
                        {
                            path: generatePathDatacentral(paths.serviceDetail, {
                                id: service_id,
                            }),
                            title: [service_id, service?.service_name]
                                ?.join(' - ')
                                ?.toUpperCase(),
                        },
                        {
                            path: '#',
                            title: t('create-service-addons'),
                        },
                    ]}
                />

                <div className="space-y-2 mt-2">
                    {exceedsParent && (
                        <Alert
                            variant="warning"
                            className="border rounded-[8px]"
                        >
                            <span className="text-orange-300 font-medium">
                                {t('addon-period-exceeds-parent', {
                                    addon_end:
                                        addonEnd?.format('DD-MM-YYYY') ?? '',
                                    parent_end:
                                        parentCurrentEnd?.format(
                                            'DD-MM-YYYY'
                                        ) ?? '',
                                })}
                            </span>
                        </Alert>
                    )}
                    {commitmentMismatch && (
                        <Alert
                            variant="warning"
                            className="border rounded-[8px]"
                        >
                            <span className="text-orange-300 font-medium">
                                {t('commitment-time-mismatch', {
                                    commitment_time:
                                        mismatchedConfig?.commitment_time ?? 0,
                                    period_months: periodMonths,
                                })}
                            </span>
                        </Alert>
                    )}
                    <Paper>
                        <div
                            data-tour="addon-service-info"
                            className="grid grid-cols-2 gap-4 text-sm"
                        >
                            <div>
                                <Typogaphy className="text-muted-foreground">
                                    {t('service-name')}:
                                </Typogaphy>
                                <Typogaphy className="font-medium mt-1 uppercase">
                                    {service?.service_name}
                                </Typogaphy>
                            </div>
                            <div>
                                <Typogaphy className="text-muted-foreground">
                                    {t('product_group')}:
                                </Typogaphy>
                                <Typogaphy className="font-medium mt-1">
                                    {service?.product_group_name}
                                </Typogaphy>
                            </div>
                            <div>
                                <Typogaphy className="text-muted-foreground">
                                    {t('customer')}:
                                </Typogaphy>
                                <Typogaphy className="font-medium mt-1">
                                    {service?.customer_name}
                                </Typogaphy>
                            </div>
                            <div>
                                <Typogaphy className="text-muted-foreground">
                                    {t('service-status')}:
                                </Typogaphy>
                                <Chip
                                    size="small"
                                    color="success"
                                    label={
                                        SERVICE_STATUS(t)?.find(
                                            (item) =>
                                                item.value ===
                                                service?.service_status
                                        )?.label
                                    }
                                />
                            </div>
                        </div>
                        <div data-tour="addon-sales-tracking">
                            <GridHookForm
                                subForm={true}
                                formControl={formControl}
                                actions={[]}
                                className="mt-4 grid-cols-1"
                                data={[
                                    {
                                        renderEditComponent: () => {
                                            return (
                                                <EmployeeSelect
                                                    control={
                                                        formControl.control
                                                    }
                                                    name="employee_sale_tracking_ids"
                                                    multiple={true}
                                                    label="employee_sale_tracking_ids"
                                                    showLabel={false}
                                                />
                                            )
                                        },
                                    },
                                ]}
                            />
                        </div>
                    </Paper>
                    <Paper data-tour="addon-actions">
                        <GridHookForm
                            formControl={formControl}
                            actions={[
                                {
                                    title: t('back-to-service'),
                                    type: 'button',
                                    variant: 'outlined',
                                    disabled: isCreating,
                                    handler: handleCancel,
                                },
                                {
                                    title: t('create-service'),
                                    type: 'submit',
                                    loading: isCreating,
                                    variant: 'contained',
                                    disabled:
                                        isCreating ||
                                        !products?.[0]?.product_id,
                                },
                            ]}
                            data={[
                                {
                                    renderEditComponent: () => {
                                        return (
                                            <div
                                                data-tour="addon-product-config"
                                                className="space-y-4"
                                            >
                                                {fields.map((item, index) => {
                                                    return (
                                                        <div
                                                            key={item?.id}
                                                            className="form-product"
                                                        >
                                                            <ProductTemplate
                                                                className="!p-0"
                                                                index={index}
                                                                remove={remove}
                                                                isAddon={true}
                                                                fixedProductGroup={
                                                                    service?.product_group_name
                                                                }
                                                            />
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        )
                                    },
                                    className: 'col-span-4',
                                },
                            ]}
                            onSubmit={handleSubmit}
                        />
                    </Paper>
                </div>
            </FormProvider>
        </ErrorBoundary>
    )
}

export default CreateAddons
